import { useState } from 'react'
import { motion } from 'framer-motion'
import { Image, Video, Maximize, Wand2 } from 'lucide-react'
import { GALLERY_ITEMS, buildGalleryUrl } from '@/lib/utils'
import { cn } from '@/lib/utils'

interface GalleryProps {
  onUsePrompt: (prompt: string, ratio: string, style: string, tool: string) => void
}

export function Gallery({ onUsePrompt }: GalleryProps) {
  const [filter, setFilter] = useState<'all' | 'image' | 'video'>('all')

  const filtered = GALLERY_ITEMS.filter(
    (item) => filter === 'all' || item.type === filter
  )

  return (
    <section id="gallery" className="py-20 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-block px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-semibold uppercase tracking-wider mb-4">
            Inspiration
          </div>
          <h2 className="font-display text-3xl md:text-4xl font-bold mb-3">
            Community Gallery
          </h2>
          <p className="text-muted-foreground max-w-md mx-auto">
            Click any item to use as a starting point
          </p>
        </div>

        <div className="flex justify-center gap-2 mb-10">
          {(['all', 'image', 'video'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={cn(
                'px-5 py-2 rounded-full text-sm font-medium transition-all capitalize',
                filter === f
                  ? 'bg-primary text-white shadow-lg shadow-primary/20'
                  : 'bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80'
              )}
            >
              {f === 'all' ? 'All' : f === 'image' ? 'Images' : 'Videos'}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filtered.map((item, index) => {
            const seed = 1000 + index
            const imageUrl = buildGalleryUrl(item.prompt, item.ratio, item.style, seed)

            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                onClick={() => onUsePrompt(item.prompt, item.ratio, item.style, item.type)}
                className="group relative rounded-2xl overflow-hidden bg-card border border-border cursor-pointer hover:border-primary/30 hover:shadow-xl hover:shadow-primary/5 hover:-translate-y-1 transition-all duration-300"
              >
                <div className="relative aspect-square overflow-hidden">
                  <img
                    src={imageUrl}
                    alt={item.prompt}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="absolute top-3 right-3">
                    <div className={cn(
                      'px-2.5 py-1 rounded-full text-xs font-medium flex items-center gap-1.5 backdrop-blur-md',
                      item.type === 'video'
                        ? 'bg-secondary/20 text-secondary border border-secondary/30'
                        : 'bg-primary/20 text-primary border border-primary/30'
                    )}>
                      {item.type === 'video' ? <Video className="w-3 h-3" /> : <Image className="w-3 h-3" />}
                      {item.type}
                    </div>
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="px-4 py-2 rounded-full bg-white/20 backdrop-blur-md text-white text-sm font-medium flex items-center gap-2">
                      <Wand2 className="w-4 h-4" /> Use This
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                    {item.prompt}
                  </p>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground/60">
                    <span className="flex items-center gap-1">
                      <Maximize className="w-3 h-3" /> {item.ratio}
                    </span>
                    <span className="capitalize">{item.style || 'Default'}</span>
                  </div>
                </div>
              </motion.div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
